import { EnumValues } from './EnumValues';
import { BusinessAccessLevelEnum } from './Account';

export const OwnedTypeEnum = {
  PERSONAL: 'personal',
  TEAM: 'team'
} as const;

export interface GetCardsRequest {
  businessId?: string;
  customerId?: string;
  walletId?: string;
  accountId?: string;
  ownedType?: EnumValues<typeof OwnedTypeEnum>;
}

// POST /api/v2/cards - creating a card for a team member.
export const CardTypeEnum = {
  VIRTUAL: 'virtual',
  PHYSICAL: 'physical',
  SUBSCRIPTION: 'subscription',
  AGENTIC: 'agentic'
} as const;

// EmergeFrontService's CardState.
export const CardStateEnum = {
  CREATED: 'created',
  ACTIVE: 'active',
  DELETED: 'deleted',
  EXPIRED: 'expired'
} as const;

export interface GetCardResponse {
  id: string;
  created: string;
  name: string;
  embossedName: string;
  panStart: string;
  panEnd: string;
  expiry: string;
  walletId: string;
  type: EnumValues<typeof CardTypeEnum>;
  state: EnumValues<typeof CardStateEnum>;
  // Null for a personal (non-business) card - see EmergeFrontService's
  // TransformCardToResponseUseCase.getAccessLevel.
  cardholderAccessLevel: EnumValues<typeof BusinessAccessLevelEnum> | null;
  cardholderId: string;
}

// A card's frozen state isn't a field on GetCardResponse - it's derived from whether an "all"
// type rule is attached (see GET/PUT /cards/{cardId}/rules, EmergeFrontService's ECardRule).
export const CardRuleTypeEnum = {
  ALL: 'all',
  ONLINE: 'online',
  INTERNATIONAL_POS: 'international_pos',
  CONTACTLESS: 'contactless',
  ATM: 'atm'
} as const;

export const CardRuleActionEnum = {
  ALLOW: 'allow',
  BLOCK: 'block'
} as const;

export interface RsCardRule {
  type: EnumValues<typeof CardRuleTypeEnum>;
  action: EnumValues<typeof CardRuleActionEnum>;
  readOnly: boolean;
}

export interface RqSetCardRule {
  type: EnumValues<typeof CardRuleTypeEnum>;
  action: EnumValues<typeof CardRuleActionEnum>;
}

export const SpendLimitTypeEnum = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  TOTAL: 'total'
} as const;

export interface RqAddCardSpendLimit {
  type: EnumValues<typeof SpendLimitTypeEnum>;
  totalAmount: number;
}

// GET /api/v1/cards/{cardId}/limits - unlike GET .../rules, only returns entries that have
// actually been set (no limit configured at all means an empty array, not one row per type).
export interface RsCardSpendLimit {
  type: EnumValues<typeof SpendLimitTypeEnum>;
  usedAmount: number;
  totalAmount: number;
  lastReset: string | null;
  nextReset: string | null;
}

/**
 * Matches EmergeFrontService's RqCreateCards (BatchCreateCardsUseCase / CardV2Controller). For
 * the business/team-member flow specifically: `shippingAddress` is accepted by the DTO but never
 * actually applied server-side outside the personal-account path, so it's always sent as null
 * here rather than building UI for a field the backend ignores; `cardProfileId` (card design) and
 * `limits`/`rules` are left for the later card-management work - all optional and safe to omit.
 */
export interface RqCreateCards {
  walletId: string;
  name: string;
  type: EnumValues<typeof CardTypeEnum>;
  limits: RqAddCardSpendLimit[];
  rules: RqSetCardRule[];
  printLabel: boolean | null;
  payFromWalletId: string | null;
  customerIds: string[];
  shippingAddress: null;
  cardProfileId: string | null;
}

export interface RsCreateCardResult {
  customerId: string;
  cardId?: string;
}

export interface RsBatchCreateCard {
  success: boolean;
  totalCharge: number;
  successfullyCharged?: boolean | null;
  results: RsCreateCardResult[];
}
